import { Box, Card, CardHeader, Container, Grid, Typography } from '@mui/material'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useTranslate } from '@tolgee/react'
import { useAdminStore } from '@tom-ui/admin'
import { Header, HighlightedText, useMinimalsTheme } from '@tom-ui/ui'
import { authStore, PATHS, slugify, withAuthenticationRequired } from '@tom-ui/utils'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'

export const MasterData = withAuthenticationRequired(() => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const theme = useMinimalsTheme()
  const [filter, setFilter] = useState('')
  const { tenantStore } = useAdminStore()
  const isNewYardEnabled = useBooleanFlagValue('new-yard', false)

  const masterDataItems = [
    {
      title: t(PATHS.masterData.vessels.title, 'Vessels'),
      subtitle: t(
        'manageVesselsDescription',
        'Maintain the vessel details needed for visits to your terminal, including draft, length & much more',
      ),
      route: PATHS.masterData.vessels.url,
      permission: PATHS.masterData.vessels.permission,
    },
    {
      title: t(PATHS.masterData.railcars.title, 'Railcars'),
      subtitle: t(
        'manageRailcarsDescription',
        'Record visiting railcar details for checking & reuse for later visits',
      ),
      route: PATHS.masterData.railcars.url,
      permission: PATHS.masterData.railcars.permission,
    },
    {
      title: t(PATHS.masterData.companies.title, 'Companies'),
      subtitle: t(
        'manageCompaniesDescription',
        'Manage your clients details for use throughout the system',
      ),
      route: PATHS.masterData.companies.url,
      permission: PATHS.masterData.companies.permission,
    },
    {
      title: t(PATHS.masterData.commoditiesAndPackaging.title, 'Commodities and Packaging'),
      subtitle: t(
        'manageCommoditiesAndPackingDescription',
        'Record your general cargo commodities & packaging for use throughout the system',
      ),
      route: PATHS.masterData.commoditiesAndPackaging.url,
      permission: PATHS.masterData.commoditiesAndPackaging.permission,
    },
    {
      title: t(PATHS.masterData.allocationRuleTemplates.title, 'Allocation Rule Templates'),
      subtitle: t(
        'manageAllocationRuleTemplatesDescription',
        'Create and manage templates for allocation rules to be used in the yard',
      ),
      route: PATHS.masterData.allocationRuleTemplates.url,
      permission: PATHS.masterData.allocationRuleTemplates.permission,
      hidden: isNewYardEnabled ? false : tenantStore.skipYardPlanning,
    },
    {
      title: t(PATHS.masterData.weightClasses.title, 'Container weight classes'),
      subtitle: t(
        'manageWeightClassesDescription',
        'Define and manage weight classes for organising your terminal',
      ),
      route: PATHS.masterData.weightClasses.url,
      permission: PATHS.masterData.weightClasses.permission,
    },
    {
      title: t(PATHS.masterData.damageCatalogue.title, 'Damage Catalogue'),
      subtitle: t(
        'manageDamageCatalogueDescription',
        'Manage the locations & types of damage your operators can record',
      ),
      route: PATHS.masterData.damageCatalogue.url,
      permission: PATHS.masterData.damageCatalogue.permission,
      hidden: !tenantStore.hasDamageCatalogue,
    },
    {
      title: t(PATHS.masterData.dangerousGoodsSettings.title, 'Dangerous Goods Settings'),
      subtitle: t(
        'manageDangerousGoodsSettingsDescription',
        'Set up your terminal UN materials list, segregation rules, stacking rules & much more',
      ),
      route: PATHS.masterData.dangerousGoodsSettings.url,
    },
    {
      title: t(PATHS.masterData.containerLengthBayMappings.title, 'Container Length Bay Mappings'),
      subtitle: t(
        'manageContainerLengthBayMappingsDescription',
        'Configure how container lengths map to occupied yard bays for future allocation and stack-in decisions',
      ),
      route: PATHS.masterData.containerLengthBayMappings.url,
      permission: PATHS.masterData.containerLengthBayMappings.permission,
      hidden: !isNewYardEnabled,
    },
    {
      title: t(PATHS.masterData.archetypes.title, 'Archetypes'),
      subtitle: t(
        'manageArchetypesDescription',
        'Map ISO codes to archetypes used on your terminal & with your clients',
      ),
      route: PATHS.masterData.archetypes.url,
      permission: PATHS.masterData.archetypes.permission,
    },
    {
      title: t(PATHS.masterData.isoCodeMappings.title, 'ISO Code Mapping'),
      subtitle: t('manageIsoCodeMappingsDescription', 'Create & manage your custom ISO codes'),
      route: PATHS.masterData.isoCodeMappings.url,
      permission: PATHS.masterData.isoCodeMappings.permission,
    },
    {
      title: t(PATHS.masterData.locationCodes.title, 'UN Location Codes'),
      subtitle: t(
        'manageLocationCodesDescription',
        'Define and manage UN location codes for use throughout the system',
      ),
      route: PATHS.masterData.locationCodes.url,
      permission: PATHS.masterData.locationCodes.permission,
    },
    {
      title: t(PATHS.masterData.containers.title, 'Containers'),
      subtitle: t(
        'manageContainersDescription',
        'Maintain container details for use throughout the system',
      ),
      route: PATHS.masterData.containers.url,
      permission: PATHS.masterData.containers.permission,
    },
    {
      title: t(PATHS.masterData.vesselServices.title, 'Vessel Services'),
      subtitle: t(
        'manageVesselServicesDescription',
        'Record the port calls for common vessel services that pass through your terminal',
      ),
      route: PATHS.masterData.vesselServices.url,
      permission: PATHS.masterData.vesselServices.permission,
    },
    {
      title: t(PATHS.masterData.externalDrivers.title, 'External Drivers'),
      subtitle: t(
        'manageExternalDriversDescription',
        'Record drivers names IDS & ban them from your terminal here',
      ),
      route: PATHS.masterData.externalDrivers.url,
      permission: PATHS.masterData.externalDrivers.permission,
    },
    {
      title: t(PATHS.masterData.stoppageReason.title, 'Stoppage Reason'),
      subtitle: t(
        'manageStoppageReasonDescription',
        'Manage reasons for stoppages within your terminal operations',
      ),
      route: PATHS.masterData.stoppageReason.url,
      permission: PATHS.masterData.stoppageReason.permission,
    },
    {
      title: t(PATHS.masterData.shift.title, 'Shift'),
      subtitle: t(
        'manageShiftDescription',
        'Define and manage work shifts for your terminal operations',
      ),
      route: PATHS.masterData.shift.url,
      permission: PATHS.masterData.shift.permission,
    },
    {
      title: t(PATHS.masterData.gang.title, 'Gang'),
      subtitle: t('manageGangDescription', 'Define and manage gangs for your terminal operations'),
      route: PATHS.masterData.gang.url,
      permission: PATHS.masterData.gang.permission,
    },
    {
      title: t(PATHS.masterData.stowageInstruction.title, 'Stowage Instructions'),
      subtitle: t(
        'manageStowageInstructionsDescription',
        'Create and manage stowage instructions for container handling',
      ),
      route: PATHS.masterData.stowageInstruction.url,
      permission: PATHS.masterData.stowageInstruction.permission,
    },
    {
      title: t(PATHS.masterData.holdTypes.title, 'Hold types'),
      subtitle: t('manageHoldTypesDescription', 'Create & manage your hold types'),
      route: PATHS.masterData.holdTypes.url,
      permission: PATHS.masterData.holdTypes.permission,
    },
  ].sort((a, b) => a.title.localeCompare(b.title))

  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: theme.palette.grey[100],
      }}
    >
      <Helmet>
        <title>{t('masterData', 'Master data')}</title>
      </Helmet>
      <Header
        title={t('masterData', 'Master data')}
        searchInputLabel={t('search', 'Search')}
        onSearchFieldChange={filter => setFilter(filter)}
        displaySearchIcon
        enableSearchField
      />
      <Container
        sx={{
          paddingY: theme.customSpacing.m,
        }}
      >
        <Grid container spacing={2}>
          {masterDataItems
            .filter(
              item =>
                item.title.toLowerCase().includes(filter.toLowerCase()) &&
                !item.hidden &&
                (!item.permission || authStore.hasPermission(item.permission)),
            )
            .map((item, index) => (
              <Grid key={index} size={{ xs: 12, md: 6, lg: 3 }}>
                <Card
                  onClick={() => navigate(item.route)}
                  style={{
                    cursor: 'pointer',
                    paddingBottom: theme.customSpacing.l,
                    height: '100%',
                  }}
                  data-cy={`master-data-card-${slugify(item.title)}`}
                >
                  <CardHeader
                    title={
                      <Typography variant='h3'>
                        <HighlightedText highlight={filter} color={theme.palette.info.main}>
                          {item.title}
                        </HighlightedText>
                      </Typography>
                    }
                    subheader={item.subtitle}
                  />
                </Card>
              </Grid>
            ))}
        </Grid>
      </Container>
    </Box>
  )
})
